(function (root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
    return;
  }

  root.LureMarketingAttribution = api;
  api.init(root);
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  const STORAGE_KEY = 'luredolls_marketing_attribution_v1';
  const CART_SYNC_KEY = 'luredolls_marketing_attribution_cart_sync_v1';
  const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
  const PRIVATE_ATTRIBUTE_KEYS = [
    '__ld_attr_version',
    '__ld_first_source',
    '__ld_first_medium',
    '__ld_first_campaign_id',
    '__ld_first_click_id',
    '__ld_first_landing_page',
    '__ld_first_click_time',
    '__ld_last_source',
    '__ld_last_medium',
    '__ld_last_campaign_id',
    '__ld_last_adgroup_id',
    '__ld_last_keyword',
    '__ld_last_content_id',
    '__ld_last_target_id',
    '__ld_last_network',
    '__ld_last_device',
    '__ld_last_click_id',
    '__ld_last_landing_page',
    '__ld_last_click_time',
  ];

  function clean(value, maxLength) {
    return String(value || '').trim().slice(0, maxLength || 250);
  }

  function parsePaidTouch(rawUrl, capturedAt) {
    let url;

    try {
      url = new URL(rawUrl, 'https://luredolls.com');
    } catch (error) {
      return null;
    }

    const params = url.searchParams;
    const source = clean(params.get('utm_source'), 80).toLowerCase();
    const medium = clean(params.get('utm_medium'), 80).toLowerCase();
    const gclid = clean(params.get('gclid'), 250);
    const wbraid = clean(params.get('wbraid'), 250);
    const gbraid = clean(params.get('gbraid'), 250);
    const clickId = gclid || wbraid || gbraid;
    const isPaidMedium = /^(cpc|ppc|paid|paid-search|paid_search|sem)$/.test(medium);

    if (!clickId && !isPaidMedium) return null;

    return {
      source: source || (clickId ? 'google' : ''),
      medium: medium || (clickId ? 'cpc' : ''),
      campaignId: clean(params.get('utm_id') || params.get('campaign_id') || params.get('utm_campaign'), 160),
      adGroupId: clean(params.get('adgroup_id'), 160),
      keyword: clean(params.get('utm_term'), 250),
      contentId: clean(params.get('utm_content'), 160),
      targetId: clean(params.get('target_id'), 160),
      network: clean(params.get('network'), 40),
      device: clean(params.get('device'), 40),
      clickId,
      clickIdType: gclid ? 'gclid' : wbraid ? 'wbraid' : gbraid ? 'gbraid' : '',
      landingPage: clean(url.pathname || '/', 500),
      capturedAt: capturedAt || new Date().toISOString(),
    };
  }

  function mergeState(existingState, touch, nowMs) {
    const currentTime = Number.isFinite(nowMs) ? nowMs : Date.now();
    const existingIsValid =
      existingState &&
      existingState.firstPaid &&
      Number(existingState.expiresAt || 0) > currentTime;

    return {
      version: 1,
      firstPaid: existingIsValid ? existingState.firstPaid : touch,
      lastPaid: touch,
      expiresAt: currentTime + MAX_AGE_MS,
    };
  }

  function buildCartAttributes(state) {
    if (!state || !state.firstPaid || !state.lastPaid) return {};

    const first = state.firstPaid;
    const last = state.lastPaid;

    return {
      __ld_attr_version: '1',
      __ld_first_source: clean(first.source, 80),
      __ld_first_medium: clean(first.medium, 80),
      __ld_first_campaign_id: clean(first.campaignId, 160),
      __ld_first_click_id: clean(first.clickId, 250),
      __ld_first_landing_page: clean(first.landingPage, 500),
      __ld_first_click_time: clean(first.capturedAt, 40),
      __ld_last_source: clean(last.source, 80),
      __ld_last_medium: clean(last.medium, 80),
      __ld_last_campaign_id: clean(last.campaignId, 160),
      __ld_last_adgroup_id: clean(last.adGroupId, 160),
      __ld_last_keyword: clean(last.keyword, 250),
      __ld_last_content_id: clean(last.contentId, 160),
      __ld_last_target_id: clean(last.targetId, 160),
      __ld_last_network: clean(last.network, 40),
      __ld_last_device: clean(last.device, 40),
      __ld_last_click_id: clean(last.clickId, 250),
      __ld_last_landing_page: clean(last.landingPage, 500),
      __ld_last_click_time: clean(last.capturedAt, 40),
    };
  }

  function readState(storage, nowMs) {
    if (!storage) return null;

    try {
      const state = JSON.parse(storage.getItem(STORAGE_KEY) || 'null');
      if (!state || Number(state.expiresAt || 0) <= nowMs) {
        storage.removeItem(STORAGE_KEY);
        return null;
      }
      return state;
    } catch (error) {
      return null;
    }
  }

  function writeState(storage, state) {
    if (!storage) return;

    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {}
  }

  function removeStoredState(rootWindow) {
    let hadStoredAttribution = false;

    try {
      hadStoredAttribution = Boolean(
        rootWindow.localStorage.getItem(STORAGE_KEY) || rootWindow.sessionStorage.getItem(CART_SYNC_KEY),
      );
      rootWindow.localStorage.removeItem(STORAGE_KEY);
      rootWindow.sessionStorage.removeItem(CART_SYNC_KEY);
    } catch (error) {}

    return hadStoredAttribution;
  }

  function cartUpdateUrl(rootWindow) {
    const shopifyRoot = rootWindow.Shopify && rootWindow.Shopify.routes && rootWindow.Shopify.routes.root;
    return `${shopifyRoot || '/'}cart/update.js`;
  }

  async function updateCartAttributes(rootWindow, attributes, signature) {
    if (!rootWindow.fetch) return;

    try {
      const previousSignature = rootWindow.sessionStorage.getItem(CART_SYNC_KEY);
      if (signature && previousSignature === signature) return;
    } catch (error) {}

    try {
      const response = await rootWindow.fetch(cartUpdateUrl(rootWindow), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ attributes }),
      });

      if (!response.ok) return;

      if (signature) {
        try {
          rootWindow.sessionStorage.setItem(CART_SYNC_KEY, signature);
        } catch (error) {}
      }
    } catch (error) {}
  }

  function privacyAllowsAttribution(rootWindow) {
    const privacy = rootWindow.Shopify && rootWindow.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.marketingAllowed !== 'function') return null;
    return privacy.marketingAllowed() === true;
  }

  function captureAndSync(rootWindow) {
    const nowMs = Date.now();
    const touch = parsePaidTouch(rootWindow.location.href, new Date(nowMs).toISOString());
    let state = readState(rootWindow.localStorage, nowMs);

    if (touch) {
      state = mergeState(state, touch, nowMs);
      writeState(rootWindow.localStorage, state);
    }

    if (!state) return;

    const attributes = buildCartAttributes(state);
    const signature = JSON.stringify(attributes);
    updateCartAttributes(rootWindow, attributes, signature);
  }

  function clearAttribution(rootWindow) {
    if (!removeStoredState(rootWindow)) return;
    const attributes = Object.fromEntries(PRIVATE_ATTRIBUTE_KEYS.map((key) => [key, '']));
    updateCartAttributes(rootWindow, attributes, '');
  }

  function getCurrentCartAttributes(rootWindow) {
    if (!rootWindow || privacyAllowsAttribution(rootWindow) !== true) return {};
    return buildCartAttributes(readState(rootWindow.localStorage, Date.now()));
  }

  function init(rootWindow) {
    if (!rootWindow || !rootWindow.document || rootWindow.__lureMarketingAttributionInitialized) return;
    rootWindow.__lureMarketingAttributionInitialized = true;

    let attempts = 0;

    function applyConsentState() {
      const allowed = privacyAllowsAttribution(rootWindow);

      if (allowed === null && attempts < 20) {
        attempts += 1;
        rootWindow.setTimeout(applyConsentState, 100);
        return;
      }

      if (allowed) {
        captureAndSync(rootWindow);
      } else if (allowed === false) {
        clearAttribution(rootWindow);
      }
    }

    rootWindow.document.addEventListener('visitorConsentCollected', applyConsentState);
    applyConsentState();
  }

  return {
    STORAGE_KEY,
    CART_SYNC_KEY,
    MAX_AGE_MS,
    PRIVATE_ATTRIBUTE_KEYS,
    parsePaidTouch,
    mergeState,
    buildCartAttributes,
    getCurrentCartAttributes,
    privacyAllowsAttribution,
    init,
  };
});
